<script setup lang="ts">
import { computed, inject } from 'vue'

import Icon from '../icon/Icon.vue'
import type { TIconName } from '../icon/types'
import { pillGroupKey } from './types'

const props = withDefaults(
  defineProps<{
    /**
     * Optional icon to show alongside the text content.
     */
    icon?: TIconName
    /**
     * A text string that is used as the pill label
     */
    label: string

    /**
     * Indicates that the pill is currently disabled.
     */
    disabled?: boolean

    /**
     * The value this pill represents. Used to determine whether the pill is active
     * by comparing against the current `modelValue`.
     */
    value?: any
  }>(),
  { value: true }
)

const localModel = defineModel(undefined)

const ctx = inject(pillGroupKey, null)

/**
 * Reactive flag indicating whether this option is currently selected.
 *
 * In a grouped/controlled context, selection state is delegated to a parent
 * control via an injected context (`ctx`). If no parent context is provided,
 * the component falls back to a local comparison against its own `localModel`
 * value.
 */
const isSelected = computed(() => {
  if (ctx) {
    return ctx.isSelected(props.value)
  } else {
    return localModel.value === props.value
  }
})

/**
 * Determinies if this pill is "latching" or not. When selected, a latched
 * control cannot be directly deselected. Deselection happens automatically when
 * selecting another latching control in the same group — think radio buttons.
 *
 * Latching is used to decide whether ARIA "pressed" or "selected" state is
 * applied to the input when it is selected.
 */
const isLatching = computed(() => {
  return !ctx?.multiple
})

/**
 * Toggles or sets this option as selected.
 *
 * If a parent control context is available, the selection is delegated to it.
 * The parent is responsible for handling how selection is applied (e.g. single
 * / latching or multiple).
 *
 * If no context is present, the component manages its own state.
 */
const select = () => {
  if (ctx) {
    ctx.setValue(props.value)
  } else {
    localModel.value = localModel.value === props.value ? null : props.value
  }
}
</script>

<template>
  <button
    class="glp-pill"
    :class="{
      'glp-pill--disabled': disabled === true,
      'glp-pill--selected': isSelected,
    }"
    :aria-pressed="isLatching ? undefined : isSelected ? 'true' : 'false'"
    :aria-selected="!isLatching ? undefined : isSelected ? 'true' : 'false'"
    @click="select"
  >
    <Icon v-if="icon" class="glp-pill__icon" :name="icon" display="none" />
    {{ label }}
  </button>
</template>

<style>
/* -------------------------------- Structure ------------------------------- */

.glp-pill {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  user-select: none;
  padding-block: var(--glp-pills-item-padding-vertical, 8px);
  padding-inline: var(--glp-pills-item-padding-horizontal, 12px);
  gap: var(--glp-pills-item-gap, 8px);
  border-radius: var(--glp-pills-item-border-radius, 1000px);
  border: 1px solid
    var(--glp-pill-border-color, var(--glp-pills-item-color-border, #b6c6cf));
  background: var(
    --glp-pill-background,
    var(--glp-pills-item-default-background, rgba(255, 255, 255, 0))
  );
  color: var(--glp-pill-foreground, var(--pills-item-foreground, #082330));
  font-weight: var(
    --glp-pill-font-weight,
    var(--glp-tabs-item-font-weight, 450)
  );
  font-size: var(--glp-tabs-item-font-size, 16px);
  line-height: var(--glp-tabs-item-size-icon, 20px);
}

.glp-pill__icon {
  --glp-icon-size: var(--glp-tabs-item-size-icon, 20px);
}

/* --------------------------------- States --------------------------------- */

.glp-pill--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.glp-pill--selected {
  --glp-pill-border-color: var(--glp-pills-item-selected-color-border, #01090c);
  --glp-pill-background: var(--glp-pills-item-selected-background, #e2eaed);
  --glp-pill-font-weight: 700;
}

.glp-pill:not([aria-selected='true']):hover {
  --glp-pill-background: var(
    --glp-pills-item-hover-background,
    rgba(1, 9, 12, 0.04)
  );
}

.glp-pill:not([aria-selected='true']):active {
  --glp-pill-background: var(
    --glp-pills-item-pressed-background,
    rgba(1, 9, 12, 0.08)
  );
}

.glp-pill:focus-visible {
  outline: 3px solid var(--glp-general-color-focus);
  outline-offset: 3px;
  isolation: isolate; /** Ensure focus ring stacks above other elements */
}
</style>
